export type ManagedPageKey = 'member' | 'shared' | 'store'

export interface ManagedPageLocaleCopy {
  eyebrow: string
  title: string
  body: string
  sectionTitle: string
  sectionBody: string
  actionTitle: string
  actionBody: string
  primaryLabel: string
}

export type ManagedPageCopy = Record<ManagedPageKey, { ko: ManagedPageLocaleCopy; en: ManagedPageLocaleCopy }>

export const defaultManagedPageCopy: ManagedPageCopy = {
  member: {
    ko: {
      eyebrow: '내 반려동물',
      title: '오늘 있었던 일을 가볍게 남겨두세요.',
      body: '사진과 짧은 메모가 쌓이면, 다음 병원에서 처음부터 다시 설명하지 않아도 됩니다.',
      sectionTitle: '무엇이 달랐나요?',
      sectionBody: '가까운 항목을 고르세요. 사진 한 장만 올려도 괜찮습니다.',
      actionTitle: '예약할 때 이 기록을 함께 보내세요.',
      actionBody: '병원은 로그인 없이 읽기 전용 화면으로 확인합니다. 만료일을 정하고 진료 후 바로 철회할 수 있습니다.',
      primaryLabel: '병원 보안 링크 만들기',
    },
    en: {
      eyebrow: 'My pets',
      title: 'Keep today’s moments without the paperwork.',
      body: 'Photos and short notes become a story you can carry to the next clinic.',
      sectionTitle: 'What changed?',
      sectionBody: 'Choose the closest option. One photo is enough.',
      actionTitle: 'Attach this record to the appointment.',
      actionBody: 'The clinic opens a read-only page without signing in. Set an expiry and revoke it after the visit.',
      primaryLabel: 'Create clinic link',
    },
  },
  shared: {
    ko: {
      eyebrow: '보호자가 정리한 기록',
      title: '지금도 보이는 변화',
      body: '집에서 계속 관찰되는 내용을 먼저 보여드립니다.',
      sectionTitle: '처음 기록부터 날짜순으로',
      sectionBody: '과거 기록부터 차례대로 표시합니다.',
      actionTitle: '보호자가 궁금한 점',
      actionBody: '걱정되거나 궁금한 내용을 보호자가 직접 적었습니다.',
      primaryLabel: '진료자료 열기',
    },
    en: {
      eyebrow: 'Notes prepared by the guardian',
      title: 'Changes still being seen',
      body: 'Ongoing observations from home are shown first.',
      sectionTitle: 'From the first note onward',
      sectionBody: 'Oldest notes are shown first.',
      actionTitle: 'Questions from the guardian',
      actionBody: 'These are concerns or questions written by the guardian.',
      primaryLabel: 'Open clinic files',
    },
  },
  store: {
    ko: {
      eyebrow: '반려동물 돌봄용품',
      title: '필요한 순간에 바로 찾을 수 있도록.',
      body: '운영진이 확인한 위생, 회복, 이동용품을 안내합니다. 결제와 배송은 연결된 판매 페이지의 정책에 따릅니다.',
      sectionTitle: '상품 안내를 준비하고 있습니다.',
      sectionBody: '검토가 끝난 상품만 순차적으로 공개합니다.',
      actionTitle: '응급 상황에서는 병원에 먼저 연락하세요.',
      actionBody: '상품 구매보다 가까운 동물병원 또는 응급진료기관의 안내가 우선입니다.',
      primaryLabel: '구매 페이지로',
    },
    en: {
      eyebrow: 'Pet care supplies',
      title: 'Ready when care cannot wait.',
      body: 'Reviewed hygiene, recovery and travel essentials. Payment and delivery follow the connected seller’s terms.',
      sectionTitle: 'The store is being prepared.',
      sectionBody: 'Products appear only after review.',
      actionTitle: 'Contact a clinic first in an emergency.',
      actionBody: 'Veterinary guidance takes priority over shopping for supplies.',
      primaryLabel: 'Go to checkout',
    },
  },
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function clean(value: unknown, fallback: string, max = 600) {
  return typeof value === 'string' ? value.trim().slice(0, max) : fallback
}

export function sanitizeManagedPageCopy(value: unknown): ManagedPageCopy {
  const source = record(value) ? value : {}
  return (Object.keys(defaultManagedPageCopy) as ManagedPageKey[]).reduce((result, page) => {
    const pageSource = record(source[page]) ? source[page] : {}
    result[page] = (['ko', 'en'] as const).reduce((locales, locale) => {
      const localeSource = record(pageSource[locale]) ? pageSource[locale] : {}
      const fallback = defaultManagedPageCopy[page][locale]
      locales[locale] = {
        eyebrow: clean(localeSource.eyebrow, fallback.eyebrow, 100),
        title: clean(localeSource.title, fallback.title, 240),
        body: clean(localeSource.body, fallback.body, 900),
        sectionTitle: clean(localeSource.sectionTitle, fallback.sectionTitle, 200),
        sectionBody: clean(localeSource.sectionBody, fallback.sectionBody, 600),
        actionTitle: clean(localeSource.actionTitle, fallback.actionTitle, 200),
        actionBody: clean(localeSource.actionBody, fallback.actionBody, 600),
        primaryLabel: clean(localeSource.primaryLabel, fallback.primaryLabel, 60),
      }
      return locales
    }, {} as ManagedPageCopy[ManagedPageKey])
    return result
  }, {} as ManagedPageCopy)
}

export function managedPageCopyIsComplete(value: ManagedPageCopy) {
  return (Object.keys(value) as ManagedPageKey[]).every((page) => (['ko', 'en'] as const).every((locale) => {
    const copy = value[page][locale]
    return Boolean(copy.title && copy.body && copy.sectionTitle && copy.actionTitle && copy.primaryLabel)
  }))
}
